import flinksLogo from './logos/flinks.svg';
import freshBooksLogo from './logos/freshBooks.svg';
import globalPaymentsLogo from './logos/globalPayments.png';
import inveriteLogo from './logos/inverite.svg';
import oracleNetSuiteLogo from './logos/oracleNetSuite.svg';
import microsoftDynamics365Logo from './logos/microsoftDynamics365.png';
import plaidLogo from './logos/plaid.svg';
import quickBooksLogo from './logos/quickBooks.svg';
import sageBusinessCloudLogo from './logos/sageBusinessCloud.svg';
import waveLogo from './logos/wave.svg';
import xeroLogo from './logos/xero.svg';
import { RAILZ_SERVICE_NAMES, RailzService } from '@/components/Intergrations/AccountingSoftware/railzServices';
import {
  FLINKS_HOW_IT_WORKS,
  GLOBAL_PAYMENTS_HOW_IT_WORKS,
  INVERITE_HOW_IT_WORKS,
  PLAID_HOW_IT_WORKS,
} from './howItWorksCatalogue';
import { IntegrationDefinition, IntegrationGroup, IntegrationId } from './types';

// Grouping is defined statically on the front end — no backend data drives it
export const INTEGRATION_GROUPS: IntegrationGroup[] = [
  'bankDataAndVerification',
  'paymentProcessing',
  'accountingAndErp',
];

// All ERP cards share one underlying integration (accounting data import) and one gate; the
// service param scopes the Railz Connect widget to the card that was clicked
function erpIntegration(id: IntegrationId, logo: string, railzService: RailzService): IntegrationDefinition {
  const name = RAILZ_SERVICE_NAMES[railzService];

  return {
    id,
    name,
    group: 'accountingAndErp',
    countries: ['CA', 'US'],
    logo,
    route: `/integrations/accounting-software?service=${railzService}`,
    railzService,
    requiresPermission: 'ViewAccountingBillsAndInvoices',
    getState: ({ AccountingSoftwareIntegrationEnabled, connectedRailzServices }) => {
      if (!AccountingSoftwareIntegrationEnabled) return 'locked';

      // Connected is per software: Railz reports which one this account authorized
      return connectedRailzServices.includes(railzService) ? 'connected' : 'available';
    },
    description: (t) =>
      t('Integrations.Catalogue.Erp.description', {
        defaultValue:
          'Integrate your accounting software to import invoices, pay bills, and manage payments from the portal.',
      }),
    prerequisites: [
      {
        id: 'activeAccount',
        label: (t) =>
          t('Integrations.Catalogue.Erp.Prerequisites.activeAccount', {
            defaultValue: 'An active {{name}} account',
            name,
          }),
      },
      {
        id: 'adminCredentials',
        label: (t) =>
          t('Integrations.Catalogue.Erp.Prerequisites.adminCredentials', {
            defaultValue: 'Admin credentials to authorize the connection',
          }),
      },
    ],
  };
}

export const INTEGRATIONS: IntegrationDefinition[] = [
  {
    id: 'flinks',
    name: 'Flinks',
    group: 'bankDataAndVerification',
    countries: ['CA'],
    logo: flinksLogo,
    setup: {
      connectedFlag: 'CustomFlinksCredentialsSet',
      fields: [
        {
          name: 'FlinksUrl',
          icon: 'link',
          label: (t) => t('Integrations.Catalogue.Flinks.Setup.Fields.url', { defaultValue: 'Flinks URL' }),
          placeholder: 'https://{instance}-api.private.fin.ag/v3/{customerId}',
        },
      ],
      howItWorks: FLINKS_HOW_IT_WORKS,
    },
    getState: ({ CustomFlinksCredentialsSet }) => (CustomFlinksCredentialsSet ? 'connected' : 'locked'),
    description: (t) =>
      t('Integrations.Catalogue.Flinks.description', {
        defaultValue:
          'Connect your existing Flinks account to securely verify bank accounts and enable EFT processing with iQ11.',
      }),
    prerequisites: [
      {
        id: 'apiUrl',
        label: (t) => t('Integrations.Catalogue.Flinks.Prerequisites.apiUrl', { defaultValue: 'Flinks API URL' }),
        example: 'https://{instance}-api.private.fin.ag/v3/{customerId}',
      },
      {
        id: 'customerId',
        label: (t) =>
          t('Integrations.Catalogue.Flinks.Prerequisites.customerId', { defaultValue: 'Customer ID ({customerId})' }),
      },
      {
        id: 'instance',
        label: (t) =>
          t('Integrations.Catalogue.Flinks.Prerequisites.instance', { defaultValue: 'Instance ({instance})' }),
      },
    ],
  },
  {
    id: 'plaid',
    name: 'Plaid',
    group: 'bankDataAndVerification',
    countries: ['CA', 'US'],
    logo: plaidLogo,
    setup: {
      connectedFlag: 'CustomPlaidCredentialsSet',
      fields: [
        {
          name: 'PlaidClientID',
          icon: 'tag',
          label: (t) => t('Integrations.Catalogue.Plaid.Setup.Fields.clientId', { defaultValue: 'Plaid Client ID' }),
        },
        {
          name: 'PlaidSecretKey',
          icon: 'password',
          label: (t) => t('Integrations.Catalogue.Plaid.Setup.Fields.secretKey', { defaultValue: 'Plaid Secret Key' }),
          isSecret: true,
        },
        {
          name: 'PlaidUrl',
          icon: 'link',
          label: (t) => t('Integrations.Catalogue.Plaid.Setup.Fields.url', { defaultValue: 'Plaid URL' }),
          placeholder: 'https://',
        },
      ],
      howItWorks: PLAID_HOW_IT_WORKS,
    },
    getState: ({ CustomPlaidCredentialsSet }) => (CustomPlaidCredentialsSet ? 'connected' : 'locked'),
    description: (t) =>
      t('Integrations.Catalogue.Plaid.description', {
        defaultValue:
          'Connect your existing Plaid account to securely verify bank accounts and enable EFT processing with iQ11.',
      }),
    prerequisites: [
      {
        id: 'clientId',
        label: (t) => t('Integrations.Catalogue.Plaid.Prerequisites.clientId', { defaultValue: 'Plaid Client ID' }),
      },
      {
        id: 'secretKey',
        label: (t) => t('Integrations.Catalogue.Plaid.Prerequisites.secretKey', { defaultValue: 'Plaid Secret Key' }),
      },
      { id: 'url', label: (t) => t('Integrations.Catalogue.Plaid.Prerequisites.url', { defaultValue: 'Plaid URL' }) },
    ],
  },
  {
    id: 'inverite',
    name: 'Inverite',
    group: 'bankDataAndVerification',
    countries: ['CA'],
    logo: inveriteLogo,
    setup: {
      connectedFlag: 'CustomInveriteCredentialsSet',
      fields: [
        {
          name: 'InveriteAPIKey',
          icon: 'api',
          label: (t) => t('Integrations.Catalogue.Inverite.Setup.Fields.apiKey', { defaultValue: 'Inverite API Key' }),
          isSecret: true,
        },
        {
          name: 'InveriteUrl',
          icon: 'link',
          label: (t) => t('Integrations.Catalogue.Inverite.Setup.Fields.url', { defaultValue: 'Inverite URL' }),
        },
      ],
      howItWorks: INVERITE_HOW_IT_WORKS,
    },
    getState: ({ CustomInveriteCredentialsSet }) => (CustomInveriteCredentialsSet ? 'connected' : 'locked'),
    description: (t) =>
      t('Integrations.Catalogue.Inverite.description', {
        defaultValue:
          'Connect Inverite to securely verify bank accounts and streamline Open Finance payment processing.',
      }),
    prerequisites: [
      {
        id: 'apiKey',
        label: (t) => t('Integrations.Catalogue.Inverite.Prerequisites.apiKey', { defaultValue: 'Inverite API Key' }),
      },
      {
        id: 'url',
        label: (t) => t('Integrations.Catalogue.Inverite.Prerequisites.url', { defaultValue: 'Inverite URL' }),
      },
    ],
  },
  {
    id: 'globalPayments',
    name: 'Global Payments',
    group: 'paymentProcessing',
    countries: ['CA', 'US'],
    logo: globalPaymentsLogo,
    setup: {
      connectedFlag: 'CustomGlobalPaySharedSecret',
      fields: [
        {
          name: 'GlobalPayAppID',
          icon: 'tag',
          label: (t) =>
            t('Integrations.Catalogue.GlobalPayments.Setup.Fields.appId', { defaultValue: 'Global Pay App ID' }),
        },
        {
          name: 'GlobalPaySharedSecret',
          icon: 'password',
          label: (t) =>
            t('Integrations.Catalogue.GlobalPayments.Setup.Fields.sharedSecret', {
              defaultValue: 'Global Pay Shared Secret',
            }),
          isSecret: true,
        },
      ],
      howItWorks: GLOBAL_PAYMENTS_HOW_IT_WORKS,
    },
    // Global Payments has no account gate, so it is never locked
    getState: ({ CustomGlobalPaySharedSecret }) => (CustomGlobalPaySharedSecret ? 'connected' : 'available'),
    description: (t) =>
      t('Integrations.Catalogue.GlobalPayments.description', {
        defaultValue:
          'Connect your existing Global Payments account to securely process credit card transactions with iQ11.',
      }),
    prerequisites: [
      {
        id: 'appId',
        label: (t) =>
          t('Integrations.Catalogue.GlobalPayments.Prerequisites.appId', { defaultValue: 'Global Pay App ID' }),
      },
      {
        id: 'sharedSecret',
        label: (t) =>
          t('Integrations.Catalogue.GlobalPayments.Prerequisites.sharedSecret', {
            defaultValue: 'Global Pay Shared Secret',
          }),
      },
    ],
  },
  erpIntegration('quickBooksOnline', quickBooksLogo, 'quickbooks'),
  erpIntegration('microsoftDynamics365', microsoftDynamics365Logo, 'dynamicsBusinessCentral'),
  erpIntegration('freshBooks', freshBooksLogo, 'freshbooks'),
  erpIntegration('oracleNetSuite', oracleNetSuiteLogo, 'oracleNetsuite'),
  erpIntegration('quickBooksDesktop', quickBooksLogo, 'quickbooksDesktop'),
  erpIntegration('sageBusinessCloud', sageBusinessCloudLogo, 'sageBusinessCloud'),
  erpIntegration('xero', xeroLogo, 'xero'),
  erpIntegration('wave', waveLogo, 'wave'),
];
